/* eslint-disable unicorn/prefer-at */
import { v4 as uuid4 } from "uuid";

import {
  ActivityStatus,
  courseEnum,
} from "@transfr-inc/dashboard-components/utils";

import { MasteryStatus, getStatusValue } from "@models";
import { OohStatus } from "@models/ooh-status";
import { ContentType } from "@transfr-inc/dashboard-components/curriculum";

import { CredentialType } from "@views/my-work/models/credential-type";

// These sims do not have any mastery data recorded ever
export const listOfExemptedSims = [
  "84e3fec2-a11d-490a-9dea-0e6d56f4099e", //Controls Tutorial: Calipers II
];

export const mapMasteries = (courseMastery) => {
  if (!courseMastery) return null;
  // ENG-23779: the unified userActivityMetrics rows (one per user/activity,
  // joined by activityId) are the only shape this layer consumes.
  return { userActivityMetrics: courseMastery?.userActivityMetrics || [] };
};

// Sets the most recent latestTimestamp on the activity, if any mastery has one
const setLatestTimestamp = (activity, ...masteries) => {
  const [latest] = masteries
    .map((m) => m?.latestTimestamp)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a));
  if (latest) activity.latestTimestamp = latest;
};

export const mapCompletedAndLockedData = (
  courseData,
  masteries,
  dependencies = {},
  courseId
) => {
  if (!courseData || !masteries) return courseData;
  // passStatus (or an exemption) is the credit signal (ENG-23779):
  // `completed` (finished at least once) must not unlock the next
  // activity.
  const userActivityMetrics = masteries?.userActivityMetrics || [];
  const masteryByActivityId = new Map(
    userActivityMetrics.map((mastery) => [String(mastery.activityId), mastery])
  );

  const activityDependencies = dependencies?.activityDependencies || {};
  const lessonDependencies = dependencies?.lessonDependencies || {};
  const unitDependencies = dependencies?.unitDependencies || {};

  const noDependencies =
    Object.keys(activityDependencies).length === 0 &&
    Object.keys(lessonDependencies).length === 0 &&
    Object.keys(unitDependencies).length === 0;
  const prerequisitesEnabled = courseData?.prerequisitesEnabled ?? false;

  let courseCompleted = true;
  let firstActivityProcessed = false;

  let completedActivities = new Set();
  const progressMap = {};
  const completionMap = {};
  let unlockedActivities = new Set();
  let completedUnits = new Set();
  let completedLessons = new Set();

  // Check if the activity is completed
  // and update the activity object with the completion date
  // and mastery score if applicable
  const isActivityCompleted = (activity) => {
    if (listOfExemptedSims.includes(activity?.simulationId)) {
      return true;
    }

    const mastery = masteryByActivityId.get(String(activity.id));
    if (!mastery) {
      activity.completed = false;
      activity.completionDate = "";
      return false;
    }

    const passed = mastery.passStatus === true || mastery.exempt === true;
    activity.completed = passed;
    activity.completionDate = passed
      ? mastery.simCompletionDate || mastery.latestTimestamp || ""
      : "";
    // masteryStatus is the single display label for every content type.
    activity.masteryStatus =
      mastery.exempt === true
        ? MasteryStatus.Exempt
        : getStatusValue(mastery.masteryStatus) ||
          mastery.masteryStatus ||
          "";
    activity.timeSpent = mastery.timeSpent;
    activity.totalAttempts = mastery.attempts;
    activity.masteryScore = mastery.score;
    activity.displayScore = mastery.displayScore;
    setLatestTimestamp(activity, mastery);
    return passed;
  };

  // Check if the activity is locked based on its dependencies
  // and the completion and unlocked status of its dependencies
  const isActivityLocked = (activityId) => {
    // If prerequisites are not enabled, unlock everything
    if (!prerequisitesEnabled) {
      return false;
    }

    const dependencyInfo = activityDependencies[activityId];
    if (!dependencyInfo) return !noDependencies;

    const { dependencyType, dependencies = [] } = dependencyInfo;
    if (dependencies.length === 0) return false;

    if (dependencyType === "all") {
      return !dependencies.every((dep) =>
        unlockedActivities.has(dep.dependsOnActivityId)
      );
    }
    if (dependencyType === "any") {
      return !dependencies.some((dep) =>
        unlockedActivities.has(dep.dependsOnActivityId)
      );
    }

    return true;
  };

  // Check if the lesson is locked based on its dependencies
  // and the completion status of its dependencies
  // and the completion status of its activities
  // If dependencyType is "all", all dependencies must be completed
  // If dependencyType is "any", at least one dependency must be completed
  const isLessonLocked = (lessonId) => {
    // If prerequisites are not enabled, unlock everything
    if (!prerequisitesEnabled) {
      return false;
    }

    const dependencyInfo = lessonDependencies[lessonId];
    if (!dependencyInfo) return !noDependencies;

    const { dependencyType, dependencies = [] } = dependencyInfo;
    if (dependencies.length === 0) return false;

    if (dependencyType === "all") {
      return !dependencies.every((dep) =>
        completedLessons.has(dep.dependsOnLessonId)
      );
    }
    if (dependencyType === "any") {
      return !dependencies.some((dep) =>
        completedLessons.has(dep.dependsOnLessonId)
      );
    }

    return true;
  };

  // Check if the unit is locked based on its dependencies
  // and the completion status of its dependencies
  // If dependencyType is "all", all dependencies must be completed
  // If dependencyType is "any", at least one dependency must be completed
  const isUnitLocked = (unitId) => {
    // If prerequisites are not enabled, unlock everything
    if (!prerequisitesEnabled) {
      return false;
    }

    const dependencyInfo = unitDependencies[unitId];
    if (!dependencyInfo) return !noDependencies;

    const { dependencyType, dependencies = [] } = dependencyInfo;
    if (dependencies.length === 0) return false;

    if (dependencyType === "all") {
      return !dependencies.every((dep) =>
        completedUnits.has(dep.dependsOnUnitId)
      );
    }
    if (dependencyType === "any") {
      return !dependencies.some((dep) =>
        completedUnits.has(dep.dependsOnUnitId)
      );
    }

    return true;
  };
  // Check if the course is completed based on the completion status of its activities
  // Set the locked status of an activity based on its dependencies
  // and the completion status of its dependencies
  const processActivities = (activities, lessonId) => {
    let allActivitiesComplete = true;
    let lessonNumerator = 0;
    let lessonDenominator = activities?.length || 0;

    if (activities)
      for (const activity of activities) {
        const completed = isActivityCompleted(activity);
        if (completed) {
          lessonNumerator += 1;
          completedActivities.add(activity.id);
        }
        if (!firstActivityProcessed) {
          activity.locked = false;
          firstActivityProcessed = true;
        } else if (noDependencies) {
          activity.locked = false;
        } else {
          activity.locked = isActivityLocked(activity.id);
        }
        if (completed && !activity.locked) {
          unlockedActivities.add(activity.id);
        }
        if (!completed || activity.locked) allActivitiesComplete = false;
      }

    if (lessonId) {
      progressMap[lessonId] = {
        numerator: lessonNumerator,
        denominator: lessonDenominator,
      };
    }

    return {
      allActivitiesComplete,
      lessonNumerator,
      lessonDenominator,
    };
  };

  // Check if the lesson is completed based on the completion status of its activities
  // Set the locked status of a lesson based on its dependencies
  // and the completion status of its dependencies
  const processLessons = (lessons, unitId, nextUnit, unitLocked = false) => {
    let allLessonsComplete = true;
    let unitNumerator = 0;
    let unitDenominator = 0;
    let previousLessonUnlocked = true;

    // If there are lesson dependencies, process locked status by lesson dependencies
    // Else, process locked status by previous lesson

    if (lessonDependencies && Object.keys(lessonDependencies).length > 0) {
      if (lessons)
        for (const [index, lesson] of lessons.entries()) {
          const nextLesson = lessons[index + 1];
          lesson.unitId = unitId;

          if (nextLesson?.id) {
            lesson.nextLessonId = nextLesson?.id;
          } else if (nextUnit) {
            lesson.nextLessonId = nextUnit.lessons[0]?.id;
          }

          const { allActivitiesComplete, lessonNumerator, lessonDenominator } =
            processActivities(lesson.activities, lesson.id);

          lesson.locked = isLessonLocked(lesson.id);
          lesson.completed = allActivitiesComplete;
          if (allActivitiesComplete) {
            completedLessons.add(lesson.id);
          }

          unitNumerator += lessonNumerator;
          unitDenominator += lessonDenominator;

          previousLessonUnlocked = allActivitiesComplete;
          if (!allActivitiesComplete) allLessonsComplete = false;
        }

      if (unitId) {
        progressMap[unitId] = {
          numerator: unitNumerator,
          denominator: unitDenominator,
        };
      }

      return {
        allLessonsComplete,
        unitNumerator,
        unitDenominator,
      };
    } else {
      if (lessons)
        for (const lesson of lessons) {
          const { allActivitiesComplete, lessonNumerator, lessonDenominator } =
            processActivities(lesson.activities, lesson.id);
          lesson.unitId = unitId;

          if (unitLocked) {
            lesson.locked = true;
          } else {
            lesson.locked = noDependencies ? false : !previousLessonUnlocked;
          }

          lesson.completed = allActivitiesComplete;
          if (allActivitiesComplete) {
            completedLessons.add(lesson.id);
          }

          unitNumerator += lessonNumerator;
          unitDenominator += lessonDenominator;
          previousLessonUnlocked = allActivitiesComplete;

          if (!allActivitiesComplete) allLessonsComplete = false;
        }
    }

    if (unitId) {
      progressMap[unitId] = {
        numerator: unitNumerator,
        denominator: unitDenominator,
      };
    }

    return {
      allLessonsComplete,
      unitNumerator,
      unitDenominator,
    };
  };

  // Check if the unit is completed based on the completion status of its lessons
  // Set the locked status of a unit based on its dependencies
  // and the completion status of its dependencies
  const processUnits = (units) => {
    let allUnitsComplete = true;
    let courseNumerator = 0;
    let courseDenominator = 0;
    let previousUnitCompleted = true;

    // If there are unit dependencies, process locked status by unit dependencies
    // Else, process locked status by previous unit
    if (unitDependencies && Object.keys(unitDependencies).length > 0) {
      if (units)
        for (const [index, unit] of units.entries()) {
          const nextUnit = units[index + 1];
          unit.locked = isUnitLocked(unit.id);

          const { allLessonsComplete, unitNumerator, unitDenominator } =
            processLessons(unit.lessons, unit.id, nextUnit, unit.locked);

          unit.completed = allLessonsComplete;
          if (allLessonsComplete) {
            completedUnits.add(unit.id);
          }

          courseNumerator += unitNumerator;
          courseDenominator += unitDenominator;

          previousUnitCompleted = allLessonsComplete;
          if (!allLessonsComplete) allUnitsComplete = false;
          if (unit.lessons?.length) {
            const lastLessonId = unit.lessons[unit.lessons.length - 1].id;
            completionMap[unit.id] = lastLessonId;
          }
        }
    } else {
      if (units)
        for (const [index, unit] of units.entries()) {
          const nextUnit = units[index + 1];
          unit.locked = noDependencies
            ? !noDependencies
            : !previousUnitCompleted;
          const { allLessonsComplete, unitNumerator, unitDenominator } =
            processLessons(unit.lessons, unit.id, nextUnit, unit.locked);

          unit.completed = allLessonsComplete;
          if (allLessonsComplete) {
            completedUnits.add(unit.id);
          }

          courseNumerator += unitNumerator;
          courseDenominator += unitDenominator;

          previousUnitCompleted = allLessonsComplete;
          if (!allLessonsComplete) allUnitsComplete = false;
          if (unit.lessons?.length) {
            const lastLessonId = unit.lessons[unit.lessons.length - 1].id;
            completionMap[unit.id] = lastLessonId;
          }
        }
    }
    const lastUnit = units?.[units.length - 1];
    if (lastUnit?.lessons?.length) {
      completionMap.courseComplete =
        lastUnit.lessons[lastUnit.lessons.length - 1].id;
    }

    progressMap[courseId] = {
      numerator: courseNumerator,
      denominator: courseDenominator,
    };

    return allUnitsComplete;
  };

  // If there are units, process the completion by units
  // If there are lessons, process the completion by lessons
  if (courseData?.units && courseData?.units.length > 0) {
    const allUnitsComplete = processUnits(courseData.units);
    if (!allUnitsComplete) courseCompleted = false;
  } else if (courseData?.lessons && courseData?.lessons.length > 0) {
    const { allLessonsComplete, unitNumerator, unitDenominator } =
      processLessons(courseData.lessons);
    if (!allLessonsComplete) courseCompleted = false;
    progressMap[courseId] = {
      numerator: unitNumerator,
      denominator: unitDenominator,
    };
  }

  courseData.completed = courseCompleted;
  courseData.progressMap = progressMap;
  courseData.completionMap = completionMap;
  return courseData;
};

export const findCurrentObjective = (courseData) => {
  // This makes all lessons into one array
  const allLessons = [
    ...(courseData.lessons || []).map((lesson) => ({
      ...lesson,
      unitId: null,
      unitOrderNumber: lesson.orderNumber,
      type: "courseLesson", // Type is added specifically so that we can differentiate between courseLessons and unitLessons
    })),
    ...(courseData.units || []).flatMap((unit) =>
      (unit.lessons || []).map((lesson) => ({
        ...lesson,
        unitId: unit.id,
        unitOrderNumber: unit.orderNumber,
        type: "unit",
      }))
    ),
  ];

  // Sort all lessons by either their parent unit orderNumbe or if no parent, sort by self orderNumber
  const sortedLessons = allLessons.sort((a, b) => {
    const aUnitOrder = a.unitId ? a.unitOrderNumber : a.orderNumber;
    const bUnitOrder = b.unitId ? b.unitOrderNumber : b.orderNumber;

    return aUnitOrder - bUnitOrder || a.orderNumber - b.orderNumber;
  });

  for (const lesson of sortedLessons) {
    if (lesson.completed || lesson.locked) continue;
    return {
      currentObjectiveId: lesson.id,
      parentId: lesson.unitId,
    };
  }

  return {
    currentObjectiveId: null,
    parentId: null,
  };
};

export const mapCredentialData = (courseCredentials, courseData) => {
  const progressMap = courseData.progressMap || {};

  const mapAccredibleInfo = (info, type) => {
    const id = info.courseId || info.unitId || info.lessonId || info.id;

    return info?.accredibleInfo
      ? [
          {
            type:
              info?.unitCredentialType || info?.lessonCredentialType || type,
            title:
              info.accredibleInfo.displayName ||
              info.accredibleInfo.groupIdentifier,
            description: info.accredibleInfo.groupDescription,
            id: id,
            progressMap: progressMap[id] ?? {},
            groupId: info.accredibleInfo.groupId,
          },
        ]
      : [];
  };

  const courseDataInfo = mapAccredibleInfo(
    courseCredentials,
    CredentialType.MAIN
  );

  const credentialsMap = new Map();

  for (const unit of courseCredentials.units || []) {
    const unitCreds = mapAccredibleInfo(unit, CredentialType.MICRO);
    for (const cred of unitCreds) credentialsMap.set(cred.id, cred);

    for (const lesson of unit.lessons || []) {
      const lessonCreds = mapAccredibleInfo(lesson, CredentialType.MICRO);
      for (const cred of lessonCreds) credentialsMap.set(cred.id, cred);
    }
  }

  for (const lesson of courseCredentials.lessons || []) {
    const lessonCreds = mapAccredibleInfo(lesson, CredentialType.MICRO);
    for (const cred of lessonCreds) credentialsMap.set(cred.id, cred);
  }

  const allLessons = [
    ...(courseData.lessons || []).map((lesson) => ({
      ...lesson,
      unitId: null,
      unitOrderNumber: lesson.orderNumber,
      type: "courseLesson",
    })),
    ...(courseData.units || []).flatMap((unit) =>
      (unit.lessons || []).map((lesson) => ({
        ...lesson,
        unitId: unit.id,
        unitOrderNumber: unit.orderNumber,
        type: "unit",
      }))
    ),
  ];

  const sortedLessons = allLessons.sort((a, b) => {
    const aUnitOrder = a.unitId ? a.unitOrderNumber : a.orderNumber;
    const bUnitOrder = b.unitId ? b.unitOrderNumber : b.orderNumber;

    return aUnitOrder - bUnitOrder || a.orderNumber - b.orderNumber;
  });

  const orderedCredentials = [];

  for (const lesson of sortedLessons) {
    const lessonCred = credentialsMap.get(lesson.id);
    if (lessonCred) {
      orderedCredentials.push(lessonCred);
    }

    if (lesson.unitId) {
      const unit = courseData.units?.find((unit) => unit.id === lesson.unitId);
      if (unit) {
        const unitLessons = unit.lessons || [];
        const isLastLessonInUnit =
          unitLessons.length > 0 &&
          unitLessons[unitLessons.length - 1].id === lesson.id;

        if (isLastLessonInUnit) {
          const unitCred = credentialsMap.get(unit.id);
          if (unitCred) {
            orderedCredentials.push(unitCred);
          }
        }
      }
    }
  }

  return {
    mainCredential: courseDataInfo[0] || null,
    courseInfo: [...(courseDataInfo.slice(1) || []), ...orderedCredentials],
  };
};

export const getPercent = (fraction) => {
  return Math.round((fraction?.numerator / fraction?.denominator) * 100);
};

export const progressPercent = (progress) => {
  if (isNaN(progress)) {
    const fraction = getPercent(progress);
    if (!fraction) return 0;
    return fraction;
  } else {
    return progress;
  }
};

export const getCredentialTypeByKey = (credentialType) => {
  switch (credentialType) {
    case "course": {
      return 1;
    }
    case "unit": {
      return 2;
    }
    case "lesson": {
      return 3;
    }
  }
};

export const getNextCredentialForLesson = (
  lesson,
  courseData,
  credentialData
) => {
  if (!lesson || !courseData || !credentialData) return [];
  const { mainCredential, courseInfo = [] } = credentialData;
  const credentialsToEarn = [];

  // Check if lesson itself has a credential
  const lessonCredential = courseInfo.find((cred) => cred.id === lesson.id);
  if (lessonCredential) {
    credentialsToEarn.push(lessonCredential);
  }

  // Check if lesson belongs to a unit and if completing this lesson would complete the unit
  if (lesson.unitId) {
    const unit = courseData.units?.find((unit) => unit.id === lesson.unitId);
    if (unit) {
      // Check if this lesson is the last lesson in the unit
      const unitLessons = unit.lessons || [];
      const isLastLessonInUnit =
        unitLessons.length > 0 &&
        unitLessons[unitLessons.length - 1].id === lesson.id;

      // Check if unit has a credential
      const unitCredential = courseInfo.find(
        (cred) => cred.id === lesson.unitId
      );

      if (isLastLessonInUnit && unitCredential) {
        credentialsToEarn.push(unitCredential);
        // Check if completing this unit would complete the course
        const isLastUnitInCourse =
          courseData.units.length > 0 &&
          courseData.units[courseData.units.length - 1].id === lesson.unitId;

        if (isLastUnitInCourse && mainCredential) {
          credentialsToEarn.push(mainCredential);
        }
      } else {
        if (credentialsToEarn.length === 0 && unitCredential) {
          credentialsToEarn.push(unitCredential);
        }
      }
    }
  } else {
    // Lesson is at course level (no unit)
    // Check if this lesson is the last lesson in the course
    const courseLessons = courseData.lessons || [];
    const isLastLessonInCourse =
      courseLessons.length > 0 &&
      courseLessons[courseLessons.length - 1].id === lesson.id;

    if (isLastLessonInCourse && mainCredential) {
      credentialsToEarn.push(mainCredential);
    }
  }

  // If no credentials found yet, look for next available credential
  if (credentialsToEarn.length === 0) {
    // Find the current unit index
    const currentUnitIndex = lesson.unitId
      ? courseData.units?.findIndex((unit) => unit.id === lesson.unitId) ?? -1
      : -1;

    // Look for next unit credential after current unit
    if (currentUnitIndex >= 0 && courseData.units) {
      for (let i = currentUnitIndex + 1; i < courseData.units.length; i++) {
        const nextUnit = courseData.units[i];
        const nextUnitCredential = courseInfo.find(
          (cred) => cred.id === nextUnit.id
        );
        if (nextUnitCredential) {
          credentialsToEarn.push(nextUnitCredential);
          break;
        }
      }
    }

    // If no unit credentials found, fall back to course credential
    if (credentialsToEarn.length === 0 && mainCredential) {
      credentialsToEarn.push(mainCredential);
    }
  }

  return credentialsToEarn;
};

// Maps activities for the activity player with proper status handling
export const mapActivitiesForActivityPlayer = (
  activities,
  masteries,
  completedAndLockedData,
  options
) => {
  const {
    courseId,
    classroomId,
    oohLaunchParameters,
    createOohContent,
    createIhContent,
    createCurriculumResourceContent,
  } = options;

  const userActivityMetrics = masteries?.userActivityMetrics || [];
  const updatedActivities =
    completedAndLockedData?.lessons[0]?.activities || [];

  return activities.map((activity) => {
    const masteryData =
      userActivityMetrics.find(
        (mastery) => String(mastery.activityId) === String(activity.id)
      ) || {};

    const isExempt =
      masteryData.exempt ||
      listOfExemptedSims.includes(activity?.simulationId) ||
      false;

    const updatedActivity = {
      ...activity,
      status:
        masteryData.passStatus === true || isExempt
          ? ActivityStatus.Complete
          : ActivityStatus.Incomplete,
      masteryStatus: isExempt
        ? MasteryStatus.Exempt
        : getStatusValue(masteryData?.masteryStatus) ||
        masteryData.masteryStatus ||
        "",
      masteryScore: masteryData.score || "",
      timeSpent: masteryData.timeSpent,
      totalAttempts: masteryData.attempts || "",
      simCompletionDate:
        masteryData.simCompletionDate || masteryData.latestTimestamp || "",
      latestTimestamp: masteryData.latestTimestamp || "",
      isComplete: masteryData.completed || false,
      simulationId: activity.simulationId || "",
      categoryCode: activity.categoryCode || "",
      displayScore: masteryData.displayScore,
    };

    const enhancedLaunchParameters = {
      ...oohLaunchParameters,
      activityId: `urn:activityId:${activity.id}:courseId:${courseId}:classroomId:${classroomId}`,
      sessionId: `urn:sessionId:${uuid4()}`,
    };

    const found = updatedActivities.find((a) => a.id === activity.id);
    updatedActivity.locked = found ? found.locked : false;

    if (
      updatedActivity.contentType === ContentType.OutOfHeadset.id ||
      updatedActivity.contentType === ContentType.Assessment.id ||
      updatedActivity.contentType === ContentType.CurriculumResources.id
    ) {
      // masteryStatus carries the OOH label (Passed/Completed/Failed) for
      // the notification display; status drives navigation control
      const oohCompletionStatus = isExempt
        ? OohStatus.EXEMPT
        : masteryData.masteryStatus || "";
      updatedActivity.masteryStatus = oohCompletionStatus; // For notification display
      updatedActivity.masteryScore = masteryData.score || "";
      updatedActivity.isComplete = isExempt
        ? true
        : masteryData.completed || false;

      // Set activity status based on the credit signal and locked state
      updatedActivity.status = updatedActivity.locked
        ? ActivityStatus.Locked
        : masteryData.passStatus === true || isExempt
        ? ActivityStatus.Complete
        : ActivityStatus.Incomplete;

      if (
        updatedActivity.contentType === ContentType.Assessment.id ||
        updatedActivity.contentType === ContentType.OutOfHeadset.id
      ) {
        updatedActivity.children = () =>
          createOohContent(enhancedLaunchParameters, updatedActivity);
      } else if (
        updatedActivity.contentType === ContentType.CurriculumResources.id
      ) {
        updatedActivity.children = () =>
          createCurriculumResourceContent(updatedActivity);
      }
    } else if (updatedActivity.contentType === ContentType.InHeadset.id) {
      // For IH activities, use masteryStatus for the notification component
      // and activityStatus for navigation control
      const { locked } = updatedActivity;

      const isMastered = masteryData.passStatus === true || isExempt;

      updatedActivity.status = locked
        ? ActivityStatus.Locked
        : isMastered
        ? ActivityStatus.Complete
        : ActivityStatus.Incomplete;

      updatedActivity.children = () =>
        createIhContent(updatedActivity, courseId, classroomId);
    } else {
      updatedActivity.status = ActivityStatus.Incomplete;
    }

    return updatedActivity;
  });
};

export const setMasteryInfoOnActivities = (activities, masteries, users) => {
  const userActivityMetrics = masteries?.userActivityMetrics || [];

  // Create shared unit and lesson maps
  const unitMap = new Map();

  // Build unit and lesson structure once, and add activities only if they belong to that unit/lesson
  for (const activity of activities) {
    // Handle Unit > Lesson > Activities hierarchy
    if (activity.unitId && !unitMap.has(activity.unitId)) {
      unitMap.set(activity.unitId, {
        unitId: activity.unitId,
        title: activity.unitTitle,
        orderNumber: activity.unitOrderNumber,
        lessons: new Map(),
        users: [],
        isVirtualUnit: false,
      });
    }

    if (activity.lessonId && activity.unitId) {
      const unit = unitMap.get(activity.unitId);
      if (unit && !unit.lessons.has(activity.lessonId)) {
        unit.lessons.set(activity.lessonId, {
          lessonId: activity.lessonId,
          title: activity.lessonTitle,
          orderNumber: activity.lessonOrderNumber,
          activities: [],
        });
      }

      // Add activity to lesson only if it belongs to this unit/lesson
      const lesson = unit?.lessons.get(activity.lessonId);
      if (
        lesson &&
        !lesson.activities.some((a) => a.id === activity.activityId)
      ) {
        lesson.activities.push(activity);
      }
    }

    // Handle Course > Lesson > Activities hierarchy
    if (activity.lessonId && !activity.unitId) {
      const virtualUnitId = `lesson-${activity.lessonId}`;

      if (!unitMap.has(virtualUnitId)) {
        unitMap.set(virtualUnitId, {
          unitId: virtualUnitId,
          title: activity.lessonTitle || "Lesson",
          orderNumber: activity.lessonOrderNumber || 0,
          lessons: new Map(),
          users: [],
          isVirtualUnit: true,
        });
      }

      const virtualUnit = unitMap.get(virtualUnitId);
      if (!virtualUnit.lessons.has(activity.lessonId)) {
        virtualUnit.lessons.set(activity.lessonId, {
          lessonId: activity.lessonId,
          title: activity.lessonTitle,
          orderNumber: activity.lessonOrderNumber,
          activities: [],
        });
      }

      const lesson = virtualUnit.lessons.get(activity.lessonId);
      if (
        lesson &&
        !lesson.activities.some((a) => a.id === activity.activityId)
      ) {
        lesson.activities.push(activity);
      }
    }
  }

  const userData = users.map((user) => {
    const userData = { ...user };

    // Map all activities to userData for each user
    for (const activity of activities) {
      const masteryData = userActivityMetrics.find(
        (mastery) =>
          mastery.userId === user.userId &&
          String(mastery.activityId) === String(activity.activityId)
      );

      const userActivity = {};
      if (masteryData) {
        userActivity.contentType = activity.contentType;
        userActivity.timeSpent = masteryData.timeSpent;
        userActivity.totalAttempts = masteryData.attempts || "";
        userActivity.simCompletionDate =
          masteryData.simCompletionDate || masteryData.latestTimestamp || "";
        userActivity.title = activity.title;
        userActivity.id = activity.activityId;
        userActivity.learningExperienceId = activity.simulationId;
        userActivity.contentId = activity.contentId;
        userActivity.userId = user.userId;
        userActivity.unitTitle = activity.unitTitle;
        userActivity.lessonTitle = activity.lessonTitle;
        userActivity.lessonId = activity.lessonId;
        userActivity.unitId = activity.unitId;
        userActivity.exempt = masteryData?.exempt || false;
        userActivity.exemptReason = masteryData?.exemptReason || "";
        userActivity.displayScore = masteryData?.displayScore;
        userActivity.masteryScore = masteryData.score || "";
        // isComplete drives the gradebook CSV export; keep it on the
        // same credit signal as status below so the export can't
        // contradict the grid.
        userActivity.isComplete =
          masteryData.passStatus === true || masteryData.exempt === true;
        // masteryStatus is the single display label for every content
        // type.
        userActivity.masteryStatus =
          masteryData.exempt === true
            ? MasteryStatus.Exempt
            : getStatusValue(masteryData.masteryStatus) ||
              masteryData.masteryStatus ||
              "";
        // passStatus (or an exemption) is the credit signal (ENG-23779):
        // a finished sim that didn't pass no longer renders Complete here.
        userActivity.status =
          masteryData.passStatus === true || masteryData.exempt === true
            ? ActivityStatus.Complete
            : ActivityStatus.Incomplete;

        userData[activity.activityId] = userActivity;
      } else {
        userActivity.id = activity.activityId;
        userActivity.learningExperienceId = activity.simulationId;
        userActivity.userId = user.userId;
        userActivity.unitTitle = activity.unitTitle;
        userActivity.lessonTitle = activity.lessonTitle;
        userActivity.lessonId = activity.lessonId;
        userActivity.unitId = activity.unitId;
        userActivity.exempt = false;
        userActivity.exemptReason = "";
        userActivity.title = activity.title;
        userActivity.contentType = activity.contentType;

        userData[activity.activityId] = userActivity;
      }
    }

    // Add user to units only if they have activities in those units
    for (const unit of unitMap.values()) {
      const hasActivitiesInUnit = unit.lessons.size > 0;
      if (
        hasActivitiesInUnit &&
        !unit.users.some((u) => u.userId === user.userId)
      ) {
        // Create unit-specific user data with only activities that belong to this unit
        const unitUserData = { ...user };

        // Get all activity IDs that belong to this unit
        const unitActivityIds = new Set();
        for (const lesson of unit.lessons.values()) {
          for (const activity of lesson.activities) {
            unitActivityIds.add(activity.activityId);
          }
        }

        // Only include activities that belong to this unit
        for (const activityId of unitActivityIds) {
          if (userData[activityId]) {
            unitUserData[activityId] = userData[activityId];
          }
        }

        unit.users.push(unitUserData);
      }
    }

    return userData;
  });

  // Get the first unit from the Map iterator
  const firstUnitEntry = unitMap.values().next().value;
  const defaultUnit = firstUnitEntry
    ? {
        ...firstUnitEntry,
        lessons: [...firstUnitEntry.lessons.values()],
      }
    : undefined;

  for (const unit of unitMap.values()) {
    unit.lessons = [...unit.lessons.values()];
  }

  return {
    defaultUnit: defaultUnit,
    unitMap: unitMap,
    activitiesWithMasteryInfo: userData,
  };
};

export const getGroupIds = (mainCredential, courseInfo) => {
  const groupIds = [];
  if (courseInfo?.length > 0) {
    for (const info of courseInfo) {
      groupIds.push(info.groupId);
    }
  }
  if (mainCredential?.groupId) {
    groupIds.push(mainCredential.groupId);
  }
  return groupIds;
};

export const formatActivityPlayerData = (courseData, currentLessonId) => {
  if (!courseData || !currentLessonId) {
    return {
      breadcrumb: [],
      nextLessonId: null,
      nextLessonUnitId: null,
    };
  }

  let nextLessonId = null;
  let nextLessonUnitId = null;
  const breadcrumb = [];

  // Check if lesson is in courseData.lessons array
  if (courseData.lessons && courseData.lessons.length > 0) {
    const lessonIndex = courseData.lessons.findIndex(
      (lesson) => lesson.id === currentLessonId
    );

    if (lessonIndex !== -1) {
      const currentLesson = courseData.lessons[lessonIndex];

      // Build breadcrumb for lesson in top-level lessons array
      breadcrumb.push(
        {
          type: courseEnum.course,
          name: courseData.title,
          courseId: courseData.id,
          index: "",
        },
        {
          type: courseEnum.lesson,
          name: currentLesson.title,
          index: lessonIndex + 1,
        }
      );

      // Determine next lesson
      if (lessonIndex < courseData.lessons.length - 1) {
        nextLessonId = courseData.lessons[lessonIndex + 1].id;
        nextLessonUnitId = null;
      } else {
        nextLessonId = null;
        nextLessonUnitId = null;
      }

      return {
        breadcrumb,
        nextLessonId,
        nextLessonUnitId,
      };
    }
  }

  // If not found in lessons array, search in units
  if (courseData.units && courseData.units.length > 0) {
    for (let unitIndex = 0; unitIndex < courseData.units.length; unitIndex++) {
      const unit = courseData.units[unitIndex];
      if (unit.lessons && unit.lessons.length > 0) {
        const lessonIndex = unit.lessons.findIndex(
          (lesson) => lesson.id === currentLessonId
        );

        if (lessonIndex !== -1) {
          const currentLesson = unit.lessons[lessonIndex];

          // Build breadcrumb for lesson in unit
          breadcrumb.push(
            {
              type: courseEnum.course,
              name: courseData.title,
              courseId: courseData.id,
              index: "",
            },
            {
              type: courseEnum.unit,
              name: unit.title,
              index: unitIndex + 1,
            },
            {
              type: courseEnum.lesson,
              name: currentLesson.title,
              index: lessonIndex + 1,
            }
          );

          // Determine next lesson
          if (lessonIndex < unit.lessons.length - 1) {
            // Next lesson is in the same unit
            nextLessonId = unit.lessons[lessonIndex + 1].id;
            nextLessonUnitId = unit.id;
          } else {
            // Last lesson in current unit, check for next unit
            if (unitIndex < courseData.units.length - 1) {
              const nextUnit = courseData.units[unitIndex + 1];
              if (nextUnit.lessons && nextUnit.lessons.length > 0) {
                nextLessonId = nextUnit.lessons[0].id;
                nextLessonUnitId = nextUnit.id;
              } else {
                nextLessonId = null;
                nextLessonUnitId = null;
              }
            } else {
              // Last lesson in last unit
              nextLessonId = null;
              nextLessonUnitId = null;
            }
          }

          return {
            breadcrumb,
            nextLessonId,
            nextLessonUnitId,
          };
        }
      }
    }
  }

  // Lesson not found anywhere
  return {
    breadcrumb: [],
    nextLessonId: null,
    nextLessonUnitId: null,
  };
};
